"use client"

import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"

import { cn } from "@shared/lib/utils"

/**
 * App-level tooltip timing. One `TooltipProvider` per shell (SystemShell /
 * MobileShell) with these values; a tooltip rendered outside any provider gets
 * a fallback provider with the same values so it never throws.
 */
export const TOOLTIP_DELAY_MS = 300
export const TOOLTIP_SKIP_DELAY_MS = 0

/** Window event that closes every open tooltip. */
export const CLOSE_TOOLTIPS_EVENT = "app:close-tooltips"

/**
 * When a menu closes, Radix restores focus to the menu's trigger, which is also
 * a tooltip trigger — and a focus event opens a tooltip. Opening is therefore
 * suppressed for this long (ms) once focus is being restored.
 */
export const TOOLTIP_SUPPRESS_AFTER_MENU_MS = 100

let suppressOpenUntil = 0

/**
 * Close every open tooltip. Called when a dropdown opens/closes and when the
 * route changes, so a tooltip never lingers over a menu or a new page.
 *
 * `suppressOpen` additionally blocks tooltips from re-opening for a short
 * window (see `TOOLTIP_SUPPRESS_AFTER_MENU_MS`): the menu returning focus to
 * its trigger must not pop the trigger's tooltip over the page again.
 */
export function closeAllTooltips(options: { suppressOpen?: boolean } = {}): void {
  if (typeof window === "undefined") return
  if (options.suppressOpen) {
    suppressOpenUntil = Date.now() + TOOLTIP_SUPPRESS_AFTER_MENU_MS
  }
  window.dispatchEvent(new Event(CLOSE_TOOLTIPS_EVENT))
}

/**
 * Close every tooltip whenever `routeKey` changes (pass the current pathname).
 * Lives here rather than inside `Tooltip` so this UI primitive stays free of a
 * router dependency; shells wire it to their location.
 */
export function useCloseTooltipsOnRouteChange(routeKey: string): void {
  React.useEffect(() => {
    closeAllTooltips()
  }, [routeKey])
}

const ProviderPresenceContext = React.createContext(false)
const TooltipCloseContext = React.createContext<(() => void) | null>(null)

type TooltipProviderProps = React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Provider
>

const TooltipProvider = ({ children, ...props }: TooltipProviderProps) => (
  <ProviderPresenceContext.Provider value>
    <TooltipPrimitive.Provider
      delayDuration={TOOLTIP_DELAY_MS}
      skipDelayDuration={TOOLTIP_SKIP_DELAY_MS}
      {...props}
    >
      {children}
    </TooltipPrimitive.Provider>
  </ProviderPresenceContext.Provider>
)

/**
 * Tooltip root with controlled open state so it can be closed from outside:
 * on trigger pointer-leave / blur, when any dropdown opens or closes, and on
 * route change. Keyboard focus still opens it (Radix behaviour) — blur closes.
 */
const Tooltip = ({
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root>) => {
  const hasProvider = React.useContext(ProviderPresenceContext)
  const isControlled = openProp !== undefined
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen)
  const open = isControlled ? openProp : uncontrolledOpen
  const openRef = React.useRef(open)
  openRef.current = open
  const onOpenChangeRef = React.useRef(onOpenChange)
  onOpenChangeRef.current = onOpenChange

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (next && Date.now() < suppressOpenUntil) return
      if (!isControlled) setUncontrolledOpen(next)
      onOpenChangeRef.current?.(next)
    },
    [isControlled]
  )

  const close = React.useCallback(() => {
    if (openRef.current) setOpen(false)
  }, [setOpen])

  React.useEffect(() => {
    window.addEventListener(CLOSE_TOOLTIPS_EVENT, close)
    return () => window.removeEventListener(CLOSE_TOOLTIPS_EVENT, close)
  }, [close])

  const root = (
    <TooltipCloseContext.Provider value={close}>
      <TooltipPrimitive.Root {...props} open={open} onOpenChange={setOpen}>
        {children}
      </TooltipPrimitive.Root>
    </TooltipCloseContext.Provider>
  )

  if (hasProvider) return root
  return (
    <TooltipPrimitive.Provider
      delayDuration={TOOLTIP_DELAY_MS}
      skipDelayDuration={TOOLTIP_SKIP_DELAY_MS}
    >
      {root}
    </TooltipPrimitive.Provider>
  )
}

const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>(({ onPointerLeave, onBlur, ...props }, ref) => {
  const close = React.useContext(TooltipCloseContext)
  return (
    <TooltipPrimitive.Trigger
      ref={ref}
      onPointerLeave={(event) => {
        onPointerLeave?.(event)
        close?.()
      }}
      onBlur={(event) => {
        onBlur?.(event)
        close?.()
      }}
      {...props}
    />
  )
})
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        // Solid surface (no /alpha): popover/popover-foreground is >= 7:1 in both
        // themes (scripts/check-contrast.mjs). Opacity-only, 100 ms fade: a
        // zoom/slide makes the box translucent while it moves and page text
        // shows through it.
        "z-50 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md",
        "px-3 py-1.5 text-xs",
        "animate-in fade-in-0 duration-100 motion-reduce:animate-none",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
